import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  usePropertyField,
  useSlotGroup,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

const applyCtaDef: SlotGroupDefinition = {
  label: 'Apply Online CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Apply Online',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

/**
 * Apply Access — dual CTA cards for the Apply Now page.
 *
 * Mirrors ResidentAccess: an Apply Online link (bound to the apply-now URL
 * token) and a Click to Call card. Placed above PropertyFeeGuide on apply-now.
 */
export function ApplyAccess() {
  const applyCta = useSlotGroup('applyaccess.apply_cta', applyCtaDef);
  const callTrackingPhone = (usePropertyField('property_call_tracking_phone') as string) || '';
  const defaultPhone = (usePropertyField('property_phone') as string) || '';
  const phone = callTrackingPhone || defaultPhone;
  const helpCtaLabel = useContentToken('applyaccess.help_cta_label');

  const heading = useContentToken('applyaccess.heading');
  const applyTitle = useContentToken('applyaccess.apply_title');
  const helpTitle = useContentToken('applyaccess.help_title');

  const applyUrl = applyCta.url || '';

  return (
    <section className="aa-section">
      <div className="aa-container">
        <EditableToken path="applyaccess.heading" as="h2" className="sr-only">
          {heading || 'Apply Access'}
        </EditableToken>
        <div className="aa-grid">
          {/* Apply Online Card */}
          <div className="aa-card">
            <div className="aa-card-icon-wrap aa-card-icon--apply">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm4 18H6V4h7v5h5v11zm-5-7H8v-2h5v2zm0 4H8v-2h5v2zm-2-8H8V7h3v2z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <EditableToken path="applyaccess.apply_title" as="h3" className="aa-card-title">
              {applyTitle || 'Apply Online'}
            </EditableToken>
            <RichTextContent path="applyaccess.apply_description" as="p" className="aa-card-desc" />
            <EditableSlotGroup groupPath="applyaccess.apply_cta" definition={applyCtaDef}>
              {applyUrl ? (
                <a
                  href={applyUrl}
                  target={applyCta._target || '_blank'}
                  rel="noopener noreferrer"
                  className="aa-btn aa-btn--primary"
                  data-vibesite-event="apply_now_clicked"
                >
                  <span>{applyCta.label || 'Apply Online'}</span>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path
                      d="M6 3l5 5-5 5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </a>
              ) : (
                <span className="aa-btn aa-btn--disabled">Apply URL Not Available</span>
              )}
            </EditableSlotGroup>
          </div>

          {/* Click to Call Card */}
          <div className="aa-card">
            <div className="aa-card-icon-wrap aa-card-icon--help">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.24 1.01l-2.21 2.2z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <EditableToken path="applyaccess.help_title" as="h3" className="aa-card-title">
              {helpTitle || 'Click to Call'}
            </EditableToken>
            <RichTextContent path="applyaccess.help_description" as="p" className="aa-card-desc" />
            <a
              href={phone ? `tel:${phone}` : '#'}
              className="dniphone dniphonehref aa-btn aa-btn--outline"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path
                  d="M4.64 1.33A.67.67 0 015.33 1H7a.67.67 0 01.64.47l.53 1.6a.67.67 0 01-.17.69L6.88 4.88a8 8 0 003.24 3.24l1.12-1.12a.67.67 0 01.69-.17l1.6.53A.67.67 0 0114 8v1.67a.67.67 0 01-.67.66A10 10 0 013.33 1.67.67.67 0 014 1h.64z"
                  fill="currentColor"
                />
              </svg>
              <EditableToken path="applyaccess.help_cta_label" as="span">
                {formatPhone(helpCtaLabel || phone)}
              </EditableToken>
            </a>
          </div>
        </div>
      </div>

      <style>{`
        .aa-section {
          padding: 2rem 0 3.5rem;
          background: white;
        }
        @media (min-width: 768px) {
          .aa-section { padding: 2.5rem 0 4rem; }
        }
        .aa-container {
          max-width: 56rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .aa-container { padding: 0 1.5rem; } }

        .aa-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        @media (min-width: 768px) {
          .aa-grid { grid-template-columns: 1fr 1fr; }
        }

        .aa-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 2.5rem 2rem;
          background: #f7f7f8;
          border: 1px solid #e8e8ea;
          border-radius: var(--vibe-radius-lg, 12px);
          transition: box-shadow 0.25s, transform 0.25s;
        }
        .aa-card:hover {
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
          transform: translateY(-2px);
        }

        .aa-card-icon-wrap {
          width: 56px; height: 56px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 14px;
          margin-bottom: 1.25rem;
        }
        .aa-card-icon--apply,
        .aa-card-icon--help {
          background: color-mix(in srgb, var(--vibe-color-primary) 12%, transparent);
          color: var(--vibe-color-primary);
        }

        .aa-card-title {
          font-family: var(--vibe-font-heading);
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--vibe-color-text);
          margin: 0 0 0.75rem;
        }
        .aa-card-desc {
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          line-height: 1.65;
          color: var(--vibe-color-text-muted);
          margin: 0 0 1.5rem;
          max-width: 22rem;
        }

        .aa-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          padding: 0.75rem 1.75rem;
          border-radius: var(--vibe-radius-md, 8px);
          text-decoration: none;
          cursor: pointer;
          transition: opacity 0.2s, transform 0.1s, box-shadow 0.2s;
          margin-top: auto;
        }
        .aa-btn:hover { opacity: 0.9; transform: translateY(-1px); }
        .aa-btn:active { transform: scale(0.98); }

        .aa-btn--primary {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, white);
          border: none;
        }
        .aa-btn--primary:hover {
          box-shadow: 0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }

        .aa-btn--outline {
          background: transparent;
          color: #000;
          border: 2px solid var(--vibe-color-primary);
        }
        .aa-btn--outline:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 6%, transparent);
        }

        .aa-btn--disabled {
          background: var(--vibe-color-border, #e5e7eb);
          color: var(--vibe-color-text-muted);
          border: none;
          cursor: default;
          opacity: 0.7;
        }
        .aa-btn--disabled:hover { opacity: 0.7; transform: none; }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ApplyAccess',
  displayName: 'Apply Access',
  tokens: {
    'applyaccess.heading': {
      label: 'Section Heading',
      type: 'text',
      defaultValue: 'Apply Access',
    },
    'applyaccess.apply_title': {
      label: 'Apply Card Title',
      type: 'text',
      defaultValue: 'Apply Online',
    },
    'applyaccess.apply_description': {
      label: 'Apply Card Description',
      type: 'richtext',
      defaultValue:
        '<p>Ready to make this community your home? Start your application online in just a few minutes. It\'s quick, secure, and available anytime.</p>',
    },
    'applyaccess.help_title': {
      label: 'Call Card Title',
      type: 'text',
      defaultValue: 'Click to Call',
    },
    'applyaccess.help_description': {
      label: 'Call Card Description',
      type: 'richtext',
      defaultValue:
        '<p>Have questions about applying, available floor plans, or move-in specials? Our leasing team is happy to help — give us a call.</p>',
    },
    'applyaccess.help_cta_label': {
      label: 'Phone CTA Label',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
      defaultValue: '',
    },
  },
  slotGroups: {
    'applyaccess.apply_cta': applyCtaDef,
  },
};

(ApplyAccess as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default ApplyAccess;
